import { Stack } from '@mui/material';
import { FlowFileConversionFormat, FlowFileConversionOperation } from 'flow-schema';

import { NotableMarkdown, Text, isDefined } from 'common';

import { FieldReferenceCodeMirror } from '../../../../Runs/Run/Drawer/ContentTabs/FieldReference/CodeMirror';
import { useReactFlowViewContext } from '../../../context/context';
import { BlockType } from '../../../types';
import { OutputDocumentUrlSection } from '../../components/OutputDocumentUrlSection';

const DEFAULT_TRUNCATE_CHARS = 1000;

/** Dispatched from blockConfigsByBlockType rather than by returnType, which this block leaves as Any. */
interface FileConversionOutputContentProps {
  returnValue: string | { fileReferences: string[] } | undefined;
  metadata: unknown;
  isFullHeightView: boolean;
}

export function FileConversionOutputContent(props: FileConversionOutputContentProps) {
  const { returnValue, isFullHeightView } = props;

  const { selectedNodeData } = useReactFlowViewContext();
  if (selectedNodeData?.uiBlockType !== BlockType.FileConversion) {
    return null;
  }
  const { config } = selectedNodeData;

  if (!isDefined(returnValue) || returnValue === '') {
    return <Text>No Output</Text>;
  }

  const format = config?.format as FlowFileConversionFormat | undefined;

  if (
    config?.operation === FlowFileConversionOperation.SplitPdf &&
    typeof returnValue !== 'string'
  ) {
    const { fileReferences } = returnValue;
    return (
      <Stack spacing={1}>
        <Text bold>Split produced {fileReferences.length} file(s)</Text>
        {fileReferences.map((fileReference, index) => (
          <OutputDocumentUrlSection
            key={fileReference}
            fileReferenceId={fileReference}
            title={`Split file ${index + 1}`}
          />
        ))}
      </Stack>
    );
  }

  if (
    ((config?.operation === FlowFileConversionOperation.Convert &&
      format === FlowFileConversionFormat.Pdf) ||
      config?.operation === FlowFileConversionOperation.CombinePdf) &&
    typeof returnValue === 'string'
  ) {
    return <OutputDocumentUrlSection fileReferenceId={returnValue} title="File Transformed" />;
  }

  if (
    config?.operation === FlowFileConversionOperation.ExtractPages &&
    typeof returnValue === 'string'
  ) {
    return <OutputDocumentUrlSection fileReferenceId={returnValue} title="Extracted Pages" />;
  }

  if (typeof returnValue === 'string') {
    if (returnValue.length > DEFAULT_TRUNCATE_CHARS) {
      const truncated = returnValue.slice(0, DEFAULT_TRUNCATE_CHARS);
      const remaining = returnValue.length - DEFAULT_TRUNCATE_CHARS;
      return (
        <Stack spacing={1}>
          <NotableMarkdown>{`
\`\`\`
${truncated}... (+${remaining} chars)
\`\`\`
`}</NotableMarkdown>
        </Stack>
      );
    }

    return <NotableMarkdown>{returnValue}</NotableMarkdown>;
  }

  return (
    <FieldReferenceCodeMirror
      content={JSON.stringify(returnValue, null, 2)}
      isFullHeightView={isFullHeightView}
    />
  );
}
